import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Book, AlertCircle } from 'lucide-react';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';
import parse from 'html-react-parser';

// Document types available in the system
const DOCUMENT_TYPES = {
  terms: {
    id: 'terms',
    title: 'Terms of Service',
    icon: <FileText className="h-5 w-5" />,
    apiType: 'terms',
  },
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    icon: <Shield className="h-5 w-5" />,
    apiType: 'privacy',
  },
  disclaimer: {
    id: 'disclaimer',
    title: 'Disclaimers',
    icon: <Book className="h-5 w-5" />,
    apiType: 'disclaimer',
  },
};

// Type for a legal document as returned from the API
interface LegalDocument {
  id: string;
  document_type: string;
  version: string;
  content: string;
  effective_date: string;
  created_at: string;
}

// Purpose: Component for displaying legal documents (Terms of Service, Privacy Policy, etc.)
export const LegalPage = () => {
  const { documentType } = useParams();
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];

  // State for the active document type and fetched document
  const [activeDocument, setActiveDocument] = useState<string>(documentType || 'terms');
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch the document whenever the active document type changes
  useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get the appropriate API type for the active document
        const apiType =
          DOCUMENT_TYPES[activeDocument as keyof typeof DOCUMENT_TYPES]?.apiType || 'terms';

        // Check if we have a shortId for context (if we're coming from an application form)
        const shortId = searchParams.get('shortId');

        logger.debug('Legal', `Fetching legal document: ${activeDocument} (API type: ${apiType})`, {
          documentType: activeDocument,
          apiType,
          hasShortId: !!shortId,
        });

        // Special handling for disclaimer - must have shortId
        if (apiType === 'disclaimer') {
          if (shortId) {
            // If we have a shortId, fetch the specific disclaimer
            const response = await api.get(`/api/legal/disclaimer-by-shortid/${shortId}`);
            logger.debug('Legal', `Received disclaimer response for ${apiType}`, {
              responseType: response.data.document_type,
            });
            setDocument(response.data);
          } else {
            // No shortId, show the info message
            setError('disclaimerInfo');
            setDocument(null);
          }
        } else {
          // For terms and privacy, just get the standard documents
          const response = await api.get(`/api/legal/documents/${apiType}`);
          logger.debug('Legal', `Received document response for ${apiType}`, {
            responseType: response.data.document_type,
          });

          // Verify document type matches what we requested
          if (response.data.document_type !== apiType) {
            logger.error('Legal', 'Document type mismatch', {
              requested: apiType,
              received: response.data.document_type,
            });
            // Force a retry with explicit document type
            const retryResponse = await api.get(`/api/legal/documents/${apiType}?_=${Date.now()}`);
            setDocument(retryResponse.data);
          } else {
            setDocument(response.data);
          }
        }
      } catch (err) {
        logger.error('Legal', 'Error fetching legal document', {
          error: err,
          documentType: activeDocument,
        });

        // Special handling for disclaimer error state
        if (activeDocument === 'disclaimer') {
          setError('disclaimerInfo');
        } else {
          setError('Unable to load document. Please try again later.');
        }
        setDocument(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (documentType) {
      setActiveDocument(documentType);
    }

    fetchDocument();
  }, [documentType, activeDocument, searchParams]);

  // Handle going back to previous page
  const handleGoBack = () => {
    navigate(-1); // Go back to the previous page in history
  };

  // Handle document type change
  const handleDocumentChange = (docType: string) => {
    logger.debug('Legal', `Changing document type to: ${docType}`, {
      from: activeDocument,
      to: docType,
    });

    // Preserve the shortId query parameter when navigating
    const shortId = searchParams.get('shortId');
    const queryString = shortId ? `?shortId=${shortId}` : '';

    // First update state to trigger the useEffect
    setActiveDocument(docType);

    // Then navigate to the new URL
    navigate(`/legal/${docType}${queryString}`);
  };

  // Format date for display (YYYY-MM-DD)
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return dateString || 'Unknown date';
    }
  };

  // Get document info for the active document
  const activeDocInfo =
    DOCUMENT_TYPES[activeDocument as keyof typeof DOCUMENT_TYPES] || DOCUMENT_TYPES.terms;

  return (
    <div className="min-h-screen bg-white">
      {/* Header with back button */}
      <header className="bg-gray-800 text-white p-4">
        <div className="container mx-auto flex items-center">
          <button
            onClick={handleGoBack}
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </button>
          <h1 className="text-xl font-semibold ml-4">Legal</h1>
        </div>
      </header>

      <div className="container mx-auto py-8 px-4">
        <div className="flex flex-col md:flex-row">
          {/* Sidebar for document navigation */}
          <aside className="w-full md:w-64 mb-6 md:mb-0">
            <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
              <h2 className="text-lg font-semibold mb-4">Legal Documents</h2>
              <nav className="space-y-1">
                {Object.entries(DOCUMENT_TYPES).map(([key, doc]) => (
                  <button
                    key={key}
                    onClick={() => handleDocumentChange(key)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${
                      activeDocument === key
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {doc.icon}
                    <span className="ml-3">{doc.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 md:ml-8">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <h1 className="text-2xl font-bold">{activeDocInfo.title}</h1>
                {document && !isLoading && (
                  <div className="mt-2 sm:mt-0 text-sm text-gray-500">
                    {document.version} • Last updated: {formatDate(document.effective_date)}
                  </div>
                )}
              </div>

              {/* Loading state */}
              {isLoading && (
                <div className="py-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mb-2"></div>
                  <p className="text-gray-600">Loading document...</p>
                </div>
              )}

              {/* Error state */}
              {error && !isLoading && (
                <div className="py-8 text-center text-gray-700">
                  {error === 'disclaimerInfo' ? (
                    <>
                      <div className="flex justify-center mb-4">
                        <Book className="h-8 w-8 text-blue-500" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">Disclaimer Information</h3>
                      <p className="mb-4">
                        Disclaimers are specific to each broker in our system. To view a broker's
                        disclaimer, please access it through their application form.
                      </p>
                      <p className="text-sm text-gray-500">
                        Brokers can customize their disclaimers in their dashboard settings.
                      </p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                      <p className="text-red-600">{error}</p>
                    </>
                  )}
                </div>
              )}

              {/* Document content */}
              {document && !isLoading && !error && (
                <div className="prose max-w-none">{parse(document.content)}</div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
